import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Form,
  Input,
  Select,
  message,
  Spin,
  Breadcrumb,
  Space,
  Alert,
  Modal,
  Switch,
  Popconfirm,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
  TeamOutlined,
  WhatsAppOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  getAudienceById,
  getAudienceMembers,
  updateAudienceMember,
  removeAudienceMember,
  bulkRemoveAudienceMembers,
  updateAudience,
  getAudienceImports,
  getInvalidContactsForJob,
  manualImport,
  uploadCsvForPreview,
  confirmImportJob,
} from "../../../services/audience.service";
import NoTableData from "../../NoTableData";
import * as XLSX from "xlsx";
import CreateAudienceFlow from "./CreateAudienceFlow";
import AudienceMarketReadyLoader from "./AudienceMarketReadyLoader";

const WhatsAppAudienceDetails = ({ audienceId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [audience, setAudience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("members");

  // Settings states matching the create flow
  const [audienceTags, setAudienceTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [visibility, setVisibility] = useState("public");

  const prevStatusRef = React.useRef(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get("tab");
    if (tabParam && ["members", "history", "settings"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Members state
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);

  // Settings state
  const [form] = Form.useForm();
  const [savingSettings, setSavingSettings] = useState(false);

  // Imports History state
  const [imports, setImports] = useState([]);
  const [importsLoading, setImportsLoading] = useState(false);
  const [importsPagination, setImportsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Invalid Contacts Modal state
  const [isInvalidModalVisible, setIsInvalidModalVisible] = useState(false);
  const [invalidContacts, setInvalidContacts] = useState([]);
  const [invalidContactsLoading, setInvalidContactsLoading] = useState(false);
  const [invalidContactsPagination, setInvalidContactsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedJobId, setSelectedJobId] = useState(null);

  // Modal state for Add Members wizard flow
  const [isAddMembersModalVisible, setIsAddMembersModalVisible] =
    useState(false);

  // Edit Member Modal state
  const [isEditMemberModalVisible, setIsEditMemberModalVisible] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [savingMember, setSavingMember] = useState(false);
  const [memberForm] = Form.useForm();

  const handleDeleteMember = async (member) => {
    try {
      await removeAudienceMember(audienceId, member.id);
      message.success("Member deleted successfully");
      fetchMembers(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Failed to delete member", error);
      message.error(
        "Failed to delete member: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleBulkDelete = async () => {
    try {
      setMembersLoading(true);
      await bulkRemoveAudienceMembers(audienceId, selectedMemberIds);
      message.success(`${selectedMemberIds.length} members deleted successfully`);
      setSelectedMemberIds([]);
      fetchMembers(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Failed to bulk delete members", error);
      message.error(
        "Failed to bulk delete members: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setMembersLoading(false);
    }
  };

  const handleEditMemberClick = (member) => {
    setEditingMember(member);
    memberForm.setFieldsValue({
      name: member.name,
      phoneNumber: member.phoneNumber,
      email: member.email,
      company: member.company,
    });
    setIsEditMemberModalVisible(true);
  };

  const handleEditMemberSubmit = async (values) => {
    try {
      setSavingMember(true);
      const data = {
        ...editingMember,
        name: values.name,
        phoneNumber: values.phoneNumber,
        email: values.email,
        company: values.company,
        isWhatsappOptedIn:
          editingMember.isWhatsappOptedIn !== undefined
            ? editingMember.isWhatsappOptedIn
            : true,
      };
      await updateAudienceMember(audienceId, editingMember.id, data);
      message.success("Audience member updated successfully.");
      setIsEditMemberModalVisible(false);
      fetchMembers(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Failed to update audience member", error);
      message.error("Could not update audience member.");
    } finally {
      setSavingMember(false);
    }
  };

  useEffect(() => {
    if (audienceId) {
      fetchAudienceDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceId]);

  useEffect(() => {
    if (audienceId && activeTab === "members") {
      fetchMembers(pagination.current, pagination.pageSize);
    } else if (audienceId && activeTab === "history") {
      fetchImports(importsPagination.current, importsPagination.pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceId, activeTab]);

  useEffect(() => {
    let interval = null;
    if (audience?.status === "IMPORTING") {
      interval = setInterval(() => {
        fetchAudienceDetails(true);
        if (activeTab === "members") {
          fetchMembers(pagination.current, pagination.pageSize, true);
        } else if (activeTab === "history") {
          fetchImports(
            importsPagination.current,
            importsPagination.pageSize,
            true,
          );
        }
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience?.status, activeTab, pagination.current, pagination.pageSize]);

  useEffect(() => {
    if (prevStatusRef.current === "IMPORTING" && audience?.status !== "IMPORTING") {
      fetchMembers(1, pagination.pageSize);
      fetchImports(1, importsPagination.pageSize, true);
    }
    prevStatusRef.current = audience?.status;
  }, [audience?.status, pagination.pageSize, importsPagination.pageSize]);

  const fetchAudienceDetails = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await getAudienceById(audienceId);
      if (res && res.data) {
        setAudience(res.data);
        form.setFieldsValue({
          name: res.data.name,
          description: res.data.description,
        });
        setAudienceTags(res.data.tags || []);
        setVisibility(
          res.data.visibility ? res.data.visibility.toLowerCase() : "public",
        );
      }
    } catch (error) {
      console.error("Failed to load audience details", error);
      message.error("Could not load audience details.");
      navigate("/campaigns/audience");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (page = 1, size = 10) => {
    try {
      setMembersLoading(true);
      const params = {
        page: page - 1,
        size,
        sort: "id,desc",
      };
      const res = await getAudienceMembers(audienceId, params);
      if (res && res.data) {
        setMembers(res.data);
        const totalCount =
          parseInt(res.headers["x-total-count"], 10) || res.data.length;
        setPagination({ current: page, pageSize: size, total: totalCount });
      }
    } catch (error) {
      console.error("Failed to load audience members", error);
      message.error("Could not load audience members.");
    } finally {
      setMembersLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchMembers(newPagination.current, newPagination.pageSize);
  };

  const handleSaveSettings = async (values) => {
    try {
      setSavingSettings(true);
      const updatedAudience = {
        ...audience,
        name: values.name,
        description: values.description,
        tags: audienceTags,
        visibility: visibility ? visibility.toUpperCase() : "PUBLIC",
      };
      const res = await updateAudience(audienceId, updatedAudience);
      if (res && res.data) {
        setAudience(res.data);
        message.success("Audience updated successfully.");
      }
    } catch (error) {
      console.error("Failed to update audience", error);
      message.error("Could not update audience settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchImports = async (page = 1, size = 10, silent = false) => {
    try {
      if (!silent) setImportsLoading(true);
      const params = {
        page: page - 1,
        size,
        sort: "id,desc",
      };
      const res = await getAudienceImports(audienceId, params);
      if (res && res.data) {
        setImports(res.data);
        const totalCount =
          parseInt(res.headers["x-total-count"], 10) || res.data.length;
        setImportsPagination({
          current: page,
          pageSize: size,
          total: totalCount,
        });
      }
    } catch (error) {
      console.error("Failed to load audience imports", error);
      message.error("Could not load import history.");
    } finally {
      setImportsLoading(false);
    }
  };

  const handleImportsTableChange = (newPagination) => {
    fetchImports(newPagination.current, newPagination.pageSize);
  };

  const fetchInvalidContacts = async (jobId, page = 1, size = 10) => {
    try {
      setInvalidContactsLoading(true);
      const params = {
        page: page - 1,
        size,
        sort: "id,desc",
      };
      const res = await getInvalidContactsForJob(jobId, params);
      if (res && res.data) {
        setInvalidContacts(res.data);
        const totalCount =
          parseInt(res.headers["x-total-count"], 10) || res.data.length;
        setInvalidContactsPagination({
          current: page,
          pageSize: size,
          total: totalCount,
        });
      }
    } catch (error) {
      console.error("Failed to load invalid contacts", error);
      message.error("Could not load invalid contacts.");
    } finally {
      setInvalidContactsLoading(false);
    }
  };

  const showInvalidContactsModal = (jobId) => {
    setSelectedJobId(jobId);
    setIsInvalidModalVisible(true);
    fetchInvalidContacts(jobId, 1, 10);
  };

  const handleInvalidContactsTableChange = (newPagination) => {
    if (selectedJobId) {
      fetchInvalidContacts(
        selectedJobId,
        newPagination.current,
        newPagination.pageSize,
      );
    }
  };

  const handleAddMembersFinish = () => {
    message.success("Members imported successfully!");
    fetchAudienceDetails();
    fetchMembers(1, pagination.pageSize);
    fetchImports(1, importsPagination.pageSize, true);
    setIsAddMembersModalVisible(false);
    setActiveTab("members");
  };

  const getStatusTag = (status) => {
    switch (status) {
      case "READY":
        return (
          <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-emerald-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-emerald-750 tw-font-inter tw-border tw-border-solid tw-border-emerald-150">
            Ready
          </span>
        );
      case "DRAFT":
        return (
          <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-slate-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-slate-650 tw-font-inter tw-border tw-border-solid tw-border-slate-200">
            Draft
          </span>
        );
      case "IMPORTING":
      case "PENDING_CONFIRMATION":
        return (
          <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-blue-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-blue-750 tw-font-inter tw-border tw-border-solid tw-border-blue-150">
            Processing
          </span>
        );
      default:
        return (
          <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-slate-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-slate-650 tw-font-inter tw-border tw-border-solid tw-border-slate-200">
            {status}
          </span>
        );
    }
  };

  const memberColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <span className="tw-font-bold tw-text-slate-800 tw-font-poppins">
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (text) => (
        <span className="tw-font-medium tw-text-slate-600 tw-font-inter">
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => (
        <span className="tw-font-medium tw-text-slate-650 tw-font-inter tw-break-all">
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      render: (text) => (
        <span className="tw-font-medium tw-text-slate-550 tw-font-inter">
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditMemberClick(record)}
            className="tw-text-slate-400 hover:tw-text-slate-600 tw-transition-colors"
          />
          <Popconfirm
            title="Are you sure you want to delete this member?"
            onConfirm={() => handleDeleteMember(record)}
            okText="Yes"
            cancelText="No"
            placement="topLeft"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              className="tw-text-slate-400 hover:tw-text-red-600 tw-transition-colors"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const importColumns = [
    {
      title: "Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (date) => (
        <span className="tw-font-semibold tw-text-slate-700 tw-font-poppins">
          {new Date(date).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      ),
    },
    {
      title: "Total Contacts",
      key: "total",
      render: (_, record) => (
        <span className="tw-font-bold tw-text-slate-800">
          {record.validCount + record.invalidCount + record.duplicateCount}
        </span>
      ),
    },
    {
      title: "WhatsApp Ready",
      dataIndex: "whatsappReadyCount",
      key: "whatsappReadyCount",
      render: (count) => (
        <span className="tw-font-bold tw-text-[#22C55E]">{count || 0}</span>
      ),
    },
    {
      title: "Email Ready",
      dataIndex: "emailReadyCount",
      key: "emailReadyCount",
      render: (count) => (
        <span className="tw-font-bold tw-text-[#5D44FF]">{count || 0}</span>
      ),
    },
    {
      title: "Invalid",
      key: "invalid",
      render: (_, record) => (
        <span className="tw-font-medium tw-text-slate-650">
          {record.invalidCount}
          {record.invalidCount > 0 && (
            <Button
              type="link"
              size="small"
              onClick={() => showInvalidContactsModal(record.id)}
              className="!tw-text-red-500 hover:!tw-text-red-655 tw-font-bold tw-p-0 tw-ml-1.5 tw-font-inter"
            >
              (View Details)
            </Button>
          )}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
    },
  ];

  const invalidContactColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Phone", dataIndex: "phoneNumber", key: "phoneNumber" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Company", dataIndex: "company", key: "company" },
    {
      title: "Reason",
      key: "reason",
      render: (_, record) => {
        let reasons = [];
        if (record.invalidEmail) reasons.push("Invalid Email");
        if (record.invalidPhone) reasons.push("Invalid Phone");
        if (reasons.length === 0 && record.invalid)
          reasons.push("Missing required fields");
        return <span className="tw-text-red-500">{reasons.join(", ")}</span>;
      },
    },
  ];

  if (loading) {
    return <AudienceMarketReadyLoader />;
  }

  if (!audience) return null;

  return (
    <div className="tw-min-h-[calc(100vh-64px)] tw-flex tw-flex-col tw-bg-white tw-w-full tw-p-8 tw-overflow-hidden">
      <style>{`
        .tw-custom-table .ant-table-body {
          overflow-y: auto !important;
        }
        .tw-custom-table .ant-table-content {
          height: auto !important;
        }
      `}</style>
      {/* Alert info banner */}
      {audience.status === "IMPORTING" && (
        <Alert
          message="Import Job in Progress"
          description="The members of this audience are currently being imported in the background. The member list and counts will update automatically when the job completes."
          type="info"
          showIcon
          className="tw-mb-6 tw-rounded-2xl tw-border-solid tw-border-blue-200/50"
        />
      )}

      {/* Header section */}
      <div className="tw-flex tw-flex-col tw-gap-4 tw-mb-6 tw-flex-shrink-0">
        <div className="tw-flex tw-items-center tw-justify-between">
          {/* Breadcrumb */}
          <Breadcrumb
            separator={<span className="tw-text-slate-300">/</span>}
            className="!tw-font-inter !tw-text-[13px]"
          >
            <Breadcrumb.Item>
              <Link
                to="/campaigns/audience"
                className="hover:!tw-text-theme tw-text-sub-text tw-font-semibold"
              >
                Audiences
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item className="tw-text-text-primary tw-font-bold">
              {audience.name}
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>

        {/* Info Split */}
        <div className="tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-start tw-gap-6">
          <div className="tw-flex tw-items-start tw-gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-sub-text hover:tw-bg-slate-50 hover:tw-text-theme tw-transition-all tw-cursor-pointer"
            >
              <ArrowLeftOutlined className="tw-text-base" />
            </button>
            <div>
              <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                <h1 className="tw-text-[22px] tw-font-bold tw-text-text-primary tw-m-0 tw-font-inter tw-leading-tight">
                  {audience.name}
                </h1>
                <div className="tw-flex tw-items-center tw-gap-2">
                  {getStatusTag(audience.status)}
                  <span
                    className={`tw-inline-flex tw-items-center tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-bold tw-font-inter tw-border tw-border-solid ${
                      audience.visibility === "PUBLIC"
                        ? "tw-bg-[#E0F2FE] tw-text-[#0284C7] tw-border-[#B0E0FC]"
                        : "tw-bg-slate-100 tw-text-sub-text tw-border-slate-200"
                    }`}
                  >
                    {audience.visibility
                      ? audience.visibility.charAt(0).toUpperCase() + audience.visibility.slice(1).toLowerCase()
                      : "N/A"}
                  </span>
                </div>
              </div>

              {/* Custom Tags row */}
              {audience.tags && Array.isArray(audience.tags) && audience.tags.length > 0 && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2.5 tw-flex-wrap">
                  {audience.tags.map((tag) => (
                    <div
                      key={tag}
                      className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-white tw-border tw-border-solid tw-border-slate-200/90 tw-rounded-full tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-bold tw-text-slate-800 tw-shadow-xs"
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="tw-text-slate-400 tw-flex-shrink-0"
                      >
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                        <line x1="7" y1="7" x2="7.01" y2="7"></line>
                      </svg>
                      <span>{tag}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Description block */}
              {audience.description && (
                <p className="tw-text-[13.5px] tw-text-sub-text tw-mt-3 tw-mb-0 tw-font-inter tw-leading-relaxed tw-max-w-2xl tw-bg-slate-50/50 tw-border tw-border-solid tw-border-slate-100 tw-rounded-xl tw-px-4 tw-py-2.5">
                  <span className="tw-font-semibold tw-text-slate-700 tw-block tw-text-[11px] tw-uppercase tw-tracking-wider tw-mb-1 tw-font-inter">
                    Description
                  </span>
                  {audience.description}
                </p>
              )}
            </div>
          </div>

          {/* Summary metrics cards */}
          <div className="tw-flex tw-gap-4 tw-flex-shrink-0 tw-w-full lg:tw-w-auto">
            <div className="tw-flex-1 lg:tw-flex-initial tw-flex tw-items-center tw-gap-4 tw-bg-white tw-px-5 tw-py-4 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100 tw-shadow-[0_2px_8px_rgba(0,0,0,0.01)]">
              <div className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#F3EFFF] tw-text-theme tw-text-lg">
                <TeamOutlined />
              </div>
              <div>
                <div className="tw-text-[10.5px] tw-font-bold tw-text-sub-text tw-uppercase tw-tracking-wider tw-font-inter">
                  Total Contacts
                </div>
                <div className="tw-text-[22px] tw-font-extrabold tw-text-text-primary tw-font-inter tw-leading-none tw-mt-1">
                  {audience.totalContacts || 0}
                </div>
              </div>
            </div>
            <div className="tw-flex-1 lg:tw-flex-initial tw-flex tw-items-center tw-gap-4 tw-bg-white tw-px-5 tw-py-4 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100 tw-shadow-[0_2px_8px_rgba(0,0,0,0.01)]">
              <div className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#EAFBF0] tw-text-[#22C55E] tw-text-lg">
                <WhatsAppOutlined />
              </div>
              <div>
                <div className="tw-text-[10.5px] tw-font-bold tw-text-sub-text tw-uppercase tw-tracking-wider tw-font-inter">
                  WhatsApp Ready
                </div>
                <div className="tw-text-[22px] tw-font-extrabold tw-text-text-primary tw-font-inter tw-leading-none tw-mt-1">
                  {audience.whatsappReadyCount || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content tabs card */}
      <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-100 tw-rounded-2xl tw-shadow-[0_4px_24px_rgba(0,0,0,0.015)] tw-flex tw-flex-col tw-flex-grow tw-overflow-hidden">
        {/* Custom Tab navigation */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100 tw-px-6 tw-py-4 tw-bg-white tw-flex-shrink-0">
          {[
            { key: "members", label: "Members" },
            { key: "history", label: "Upload History" },
            { key: "settings", label: "Settings" },
          ].map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`tw-px-5 tw-py-2.5 tw-text-[13px] tw-font-bold tw-rounded-xl tw-border-0 tw-cursor-pointer tw-transition-all tw-font-inter ${
                  active
                    ? "tw-bg-[#EBE8FD] tw-text-theme"
                    : "tw-bg-transparent tw-text-sub-text hover:tw-bg-slate-50 hover:tw-text-text-primary"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content area */}
        <div className="tw-flex-1 tw-overflow-hidden tw-flex tw-flex-col">
          {activeTab === "members" && (
            <div className="tw-p-6 tw-flex-grow tw-flex tw-flex-col tw-h-full">
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-5 tw-flex-shrink-0">
                <h3 className="tw-text-[16px] tw-font-bold tw-m-0 tw-font-inter tw-text-text-primary">
                  Audience Members
                </h3>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => fetchMembers(1, pagination.pageSize)}
                    className="tw-h-9 tw-px-4 !tw-bg-white hover:!tw-bg-slate-50 !tw-border-slate-200 !tw-rounded-xl tw-font-semibold tw-text-sub-text tw-font-inter"
                  >
                    Refresh
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => setIsAddMembersModalVisible(true)}
                    className="tw-h-9 tw-px-4 !tw-bg-[#5D44FF] hover:!tw-bg-[#4F36F2] !tw-border-0 !tw-rounded-xl tw-font-bold tw-text-white tw-font-inter !tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)]"
                  >
                    Add Members
                  </Button>
                </Space>
              </div>

              {members.length === 0 && !membersLoading ? (
                <div className="tw-py-16 tw-flex-grow tw-flex tw-flex-col tw-justify-center tw-items-center tw-bg-slate-50/50 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100">
                  <NoTableData text="No members found in this audience" />
                  <div className="tw-text-center tw-mt-5">
                    <Button
                      type="primary"
                      onClick={() => setIsAddMembersModalVisible(true)}
                      className="tw-h-10 tw-px-6 !tw-bg-[#5D44FF] hover:!tw-bg-[#4F36F2] !tw-border-0 !tw-rounded-xl tw-font-bold tw-text-white tw-font-inter"
                    >
                      Import Members Now
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="tw-flex-grow tw-overflow-hidden">
                  {selectedMemberIds.length > 0 && (
                    <div className="tw-mb-4">
                      <Popconfirm
                        title={`Are you sure you want to delete ${selectedMemberIds.length} selected members?`}
                        onConfirm={handleBulkDelete}
                        okText="Yes"
                        cancelText="No"
                        placement="topLeft"
                      >
                        <Button type="primary" danger icon={<DeleteOutlined />}>
                          Bulk Delete ({selectedMemberIds.length})
                        </Button>
                      </Popconfirm>
                    </div>
                  )}
                  <Table
                    rowSelection={{
                      selectedRowKeys: selectedMemberIds,
                      onChange: setSelectedMemberIds,
                    }}
                    columns={memberColumns}
                    dataSource={members}
                    rowKey="id"
                    pagination={{
                      ...pagination,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} members`,
                    }}
                    loading={membersLoading}
                    onChange={handleTableChange}
                    className="tw-custom-table tw-custom-table-no-border"
                    scroll={{ y: "calc(100vh - 440px)" }}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === "history" && (
            <div className="tw-p-6 tw-flex-grow tw-flex tw-flex-col tw-h-full">
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-5 tw-flex-shrink-0">
                <h3 className="tw-text-[16px] tw-font-bold tw-m-0 tw-font-inter tw-text-text-primary">
                  Upload History
                </h3>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => fetchImports(1, importsPagination.pageSize)}
                  className="tw-h-9 tw-px-4 !tw-bg-white hover:!tw-bg-slate-50 !tw-border-slate-200 !tw-rounded-xl tw-font-semibold tw-text-sub-text tw-font-inter"
                >
                  Refresh
                </Button>
              </div>

              {imports.length === 0 && !importsLoading ? (
                <div className="tw-py-16 tw-flex-grow tw-flex tw-flex-col tw-justify-center tw-items-center tw-bg-slate-50/50 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100">
                  <NoTableData text="No history found" />
                </div>
              ) : (
                <div className="tw-flex-grow tw-overflow-hidden">
                  <Table
                    columns={importColumns}
                    dataSource={imports}
                    rowKey="id"
                    pagination={{
                      ...importsPagination,
                      showSizeChanger: true,
                    }}
                    loading={importsLoading}
                    onChange={handleImportsTableChange}
                    className="tw-custom-table tw-custom-table-no-border"
                    scroll={{ y: "calc(100vh - 440px)" }}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === "settings" && (
            <div className="tw-p-8 tw-max-w-3xl tw-overflow-y-auto tw-h-full">
              <h3 className="tw-text-[16px] tw-font-bold tw-mb-6 tw-font-inter tw-text-text-primary">
                Audience Settings
              </h3>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSaveSettings}
                requiredMark={false}
              >
                <Form.Item
                  label={
                    <span className="tw-text-[11.5px] tw-font-bold tw-text-text-primary tw-font-inter tw-uppercase tw-tracking-wider">
                      Audience Name
                    </span>
                  }
                  name="name"
                  rules={[
                    { required: true, message: "Please enter audience name" },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="E.g., Q4 Enterprise Leads"
                    className="!tw-h-11 !tw-rounded-xl !tw-border-slate-250 !tw-text-text-primary focus:!tw-border-theme focus:!tw-shadow-[0_0_0_2px_rgba(93,68,255,0.08)] !tw-font-inter"
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className="tw-text-[11.5px] tw-font-bold tw-text-text-primary tw-font-inter tw-uppercase tw-tracking-wider">
                      Description
                    </span>
                  }
                  name="description"
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="Add notes or description about this audience..."
                    className="!tw-rounded-xl !tw-border-slate-250 !tw-text-text-primary focus:!tw-border-theme focus:!tw-shadow-[0_0_0_2px_rgba(93,68,255,0.08)] !tw-font-inter"
                  />
                </Form.Item>

                {/* Tags block */}
                <div className="tw-mb-6">
                  <span className="tw-mb-1.5 tw-block tw-text-[11.5px] tw-font-bold tw-text-text-primary tw-font-inter tw-uppercase tw-tracking-wider">
                    Tags
                  </span>
                  <div className="tw-mt-1.5 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onPressEnter={(e) => {
                        e.preventDefault();
                        const trimmed = tagInput.trim();
                        if (trimmed && !audienceTags.includes(trimmed)) {
                          setAudienceTags([...audienceTags, trimmed]);
                        }
                        setTagInput("");
                      }}
                      placeholder="Select or type tags and press enter..."
                      className="!tw-h-11 !tw-w-[340px] !tw-rounded-xl !tw-border-slate-250 !tw-text-text-primary !tw-text-[14px] !tw-font-medium !tw-font-inter focus:!tw-border-theme focus:!tw-shadow-[0_0_0_2px_rgba(93,68,255,0.08)]"
                    />
                    <Button
                      onClick={() => {
                        const trimmed = tagInput.trim();
                        if (trimmed && !audienceTags.includes(trimmed)) {
                          setAudienceTags([...audienceTags, trimmed]);
                        }
                        setTagInput("");
                      }}
                      className="!tw-h-11 !tw-w-11 !tw-flex !tw-items-center !tw-justify-center !tw-bg-[#F1F3F5] hover:!tw-bg-[#E2E6EA] !tw-border-slate-250 !tw-rounded-xl !tw-text-slate-750 !tw-text-lg !tw-font-bold"
                    >
                      +
                    </Button>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                      {audienceTags.map((tag) => (
                        <div
                          key={tag}
                          className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-white tw-border tw-border-solid tw-border-slate-200/90 tw-rounded-full tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-bold tw-text-slate-800 tw-shadow-xs"
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="tw-text-slate-400 tw-flex-shrink-0"
                          >
                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                            <line x1="7" y1="7" x2="7.01" y2="7"></line>
                          </svg>
                          <span>{tag}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setAudienceTags(
                                audienceTags.filter((t) => t !== tag),
                              )
                            }
                            className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-slate-400 hover:tw-text-rose-500 tw-cursor-pointer tw-ml-1 tw-flex tw-items-center tw-text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Visibility Block */}
                <div className="tw-mb-8">
                  <span className="tw-mb-1.5 tw-block tw-text-[11.5px] tw-font-bold tw-text-text-primary tw-font-inter tw-uppercase tw-tracking-wider">
                    Visibility
                  </span>
                  <div className="tw-flex tw-items-center tw-h-11">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <span className="tw-text-[13.5px] tw-font-semibold tw-text-slate-600 tw-font-inter">
                        Public
                      </span>
                      <Switch
                        checked={visibility === "private"}
                        onChange={(checked) =>
                          setVisibility(checked ? "private" : "public")
                        }
                      />
                      <span className="tw-text-[13.5px] tw-font-semibold tw-text-slate-600 tw-font-inter">
                        Private
                      </span>
                    </div>
                  </div>
                  <span className="tw-text-sub-text tw-text-[12px] tw-font-inter tw-mt-2 tw-block">
                    Private audiences can only be seen by you. Public audiences
                    can be used by anyone in your workspace.
                  </span>
                </div>

                <Form.Item className="tw-mt-8 tw-mb-0">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={savingSettings}
                    icon={<SaveOutlined />}
                    size="large"
                    className="!tw-h-11 tw-px-6 !tw-bg-[#5D44FF] hover:!tw-bg-[#4F36F2] !tw-border-0 !tw-rounded-xl tw-font-bold tw-text-white tw-font-inter !tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)]"
                  >
                    Save Settings
                  </Button>
                </Form.Item>
              </Form>
            </div>
          )}
        </div>
      </div>

      <Modal
        title={null}
        visible={isInvalidModalVisible}
        onCancel={() => setIsInvalidModalVisible(false)}
        footer={null}
        centered
        width={800}
        className="delete-popup"
      >
        <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border-b tw-border-solid tw-border-slate-100/50">
          Invalid Contacts
        </div>
        <div className="tw-p-6">
          <Table
            columns={invalidContactColumns}
            dataSource={invalidContacts}
            rowKey="id"
            pagination={{
              ...invalidContactsPagination,
              showSizeChanger: true,
            }}
            loading={invalidContactsLoading}
            onChange={handleInvalidContactsTableChange}
            size="small"
          />
        </div>
        <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-3.5 tw-px-6 tw-items-center tw-justify-end tw-border-t tw-border-solid tw-border-slate-100/50">
          <Button
            className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
            onClick={() => setIsInvalidModalVisible(false)}
          >
            Close
          </Button>
        </div>
      </Modal>

      <Modal
        title={null}
        visible={isAddMembersModalVisible}
        onCancel={() => setIsAddMembersModalVisible(false)}
        footer={null}
        width={1050}
        centered
        destroyOnClose
        className="add-members-wizard-modal"
      >
        <div className="tw-p-1">
          <CreateAudienceFlow
            existingAudienceId={audienceId}
            onFinish={handleAddMembersFinish}
            onBack={() => setIsAddMembersModalVisible(false)}
          />
        </div>
      </Modal>

      <Modal
        title={null}
        open={isEditMemberModalVisible}
        onCancel={() => setIsEditMemberModalVisible(false)}
        footer={null}
        destroyOnClose
        centered
        width={480}
        className="delete-popup"
      >
        <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border-b tw-border-solid tw-border-slate-100/50">
          Edit Audience Member
        </div>
        <Form
          form={memberForm}
          layout="vertical"
          onFinish={handleEditMemberSubmit}
        >
          <div className="tw-p-6 tw-flex tw-flex-col tw-gap-1">
            <Form.Item
              name="name"
              label={<span className="tw-text-xs tw-font-semibold tw-text-slate-700">Name</span>}
              rules={[{ required: true, message: "Please enter a name" }]}
              className="!tw-mb-4"
            >
              <Input placeholder="Enter member name" className="tw-h-10 tw-rounded-lg tw-border-slate-200" />
            </Form.Item>

            <Form.Item
              name="phoneNumber"
              label={<span className="tw-text-xs tw-font-semibold tw-text-slate-700">Phone Number</span>}
              rules={[{ required: true, message: "Please enter phone number" }]}
              className="!tw-mb-4"
            >
              <Input placeholder="Enter phone number" className="tw-h-10 tw-rounded-lg tw-border-slate-200" />
            </Form.Item>

            <Form.Item
              name="email"
              label={<span className="tw-text-xs tw-font-semibold tw-text-slate-700">Email</span>}
              className="!tw-mb-4"
            >
              <Input placeholder="Enter email address" className="tw-h-10 tw-rounded-lg tw-border-slate-200" />
            </Form.Item>

            <Form.Item
              name="company"
              label={<span className="tw-text-xs tw-font-semibold tw-text-slate-700">Company</span>}
              className="!tw-mb-4"
            >
              <Input placeholder="Enter company name" className="tw-h-10 tw-rounded-lg tw-border-slate-200" />
            </Form.Item>
          </div>

          <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border-t tw-border-solid tw-border-slate-100/50">
            <Button
              className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
              onClick={() => setIsEditMemberModalVisible(false)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={savingMember}
              className="!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default WhatsAppAudienceDetails;
